/* eslint-disable no-shadow */
import { storePagination, sorting, pagination, companyDomains } from '@/mixins'
import axios from '@axios'

const getInitialState = () => ({
  ...storePagination.state(),
  permitLicenses: [],
  currentPermitLicenses: [],
  pagingByType: {
    FireDepartment: {
      currentPage: 1,
      pageCount: 1,
      pageSize: 10,
      rowCount: 0,
      rowsInCurrentPage: 0
    },
    Operating: {
      currentPage: 1,
      pageCount: 1,
      pageSize: 10,
      rowCount: 0,
      rowsInCurrentPage: 0
    },
    RentalContract: {
      currentPage: 1,
      pageCount: 1,
      pageSize: 10,
      rowCount: 0,
      rowsInCurrentPage: 0
    },
    RentalAdjust: {
      currentPage: 1,
      pageCount: 1,
      pageSize: 10,
      rowCount: 0,
      rowsInCurrentPage: 0
    },
    PropertyTax: {
      currentPage: 1,
      pageCount: 1,
      pageSize: 10,
      rowCount: 0,
      rowsInCurrentPage: 0
    }
  },
  paging: {
    ...pagination.data().paging,
    pageSize: 10
  },
  sorting: {
    ...sorting.data().sorting,
    sortBy: 'permitLicenseDueDate',
    sortDesc: false
  },
})

export default {
  namespaced: true,

  state: getInitialState(),

  getters: {
    getCurrentPermitLicensesByType: state => type => state.currentPermitLicenses.find(pl => pl.type === type) || {
      type,
      permitLicenses: [],
      totalCount: 0,
      expiredCount: 0
    },

    getPermitLicensesStats: state => {
      const totalLicenses = state.currentPermitLicenses.reduce((acc, type) => acc + type.totalCount, 0)
      const expiredLicenses = state.currentPermitLicenses.reduce((acc, type) => acc + type.expiredCount, 0)
      const validLicenses = totalLicenses - expiredLicenses

      return {
        total: totalLicenses,
        valid: validLicenses,
        expired: expiredLicenses
      }
    }
  },

  mutations: {
    ...storePagination.mutations,
    SET_CURRENT_PERMIT_LICENSES(state, currentPermitLicenses) {
      state.currentPermitLicenses = currentPermitLicenses
    },
    SET_PAGE_SIZE(state, pageSize) {
      state.paging.pageSize = pageSize
    },
    SET_PAGING(state, paging) {
      state.paging = { ...state.paging, ...paging }
    },
    SET_PAGING_BY_TYPE(state, { type, paging }) {
      if (state.pagingByType[type]) {
        state.pagingByType[type] = { ...state.pagingByType[type], ...paging }
      }
    },
    SET_TYPE_CURRENT_PAGE(state, { type, page }) {
      if (state.pagingByType[type]) {
        state.pagingByType[type].currentPage = page
      }
    },
  },

  actions: {
    ...storePagination.actions,

    async fetchCurrentPermitLicensesByType({ commit, state }, { type, params = {} }) {
      const permitLicenseTypesArray = companyDomains.methods.permitLicensesTypes()
      const permitLicenseTypes = permitLicenseTypesArray.reduce((acc, item) => {
        acc[item.value] = item.label
        return acc
      }, {})

      try {
        const currentPaging = state.pagingByType[type]
        const requestParams = {
          pageIndex: currentPaging.currentPage - 1,
          pageSize: currentPaging.pageSize,
          permitLicenseType: type,
          ...params
        }

        const { data } = await axios.get('/api/expired-permit-licenses', {
          params: requestParams
        })

        commit('SET_PAGING_BY_TYPE', {
          type,
          paging: {
            currentPage: data.currentPage + 1,
            pageCount: data.pageCount,
            pageSize: data.pageSize,
            rowCount: data.rowCount,
            rowsInCurrentPage: data.rowsInCurrentPage
          }
        })

        const processedLicenses = data.results.map(permitLicense => {
          let elapsedTimeFormat = ''
          let color = 'secondary'
          let isExpired = false

          // Empresa ativa sem documento cadastrado para este tipo
          if (!permitLicense.hasDocument) {
            return {
              ...permitLicense,
              tradingName: permitLicense.storeTradingName,
              companyId: permitLicense.companyId,
              elapsedTime: 'Sem documento',
              color: 'danger',
              isExpired: false,
              isMissing: true,
              daysDiff: 0
            }
          }

          const dueDate = new Date(permitLicense.dueDate)
          const nowDate = new Date()
          const daysDiff = Math.ceil((dueDate - nowDate) / (1000 * 60 * 60 * 24))

          if (daysDiff < 0) {
            elapsedTimeFormat = 'Vencido'
            color = 'danger'
            isExpired = true
          } else if (daysDiff <= 4) {
            elapsedTimeFormat = `${daysDiff} dias`
            color = 'warning'
          } else if (daysDiff <= 15) {
            elapsedTimeFormat = `${daysDiff} dias`
            color = 'info'
          } else if (daysDiff <= 30) {
            elapsedTimeFormat = `${daysDiff} dias`
            color = 'dark'
          } else if (daysDiff <= 60) {
            elapsedTimeFormat = `${daysDiff} dias`
            color = 'success'
          } else {
            elapsedTimeFormat = `${daysDiff} dias`
            color = 'secondary'
          }

          return {
            ...permitLicense,
            tradingName: permitLicense.storeTradingName,
            companyId: permitLicense.companyId,
            elapsedTime: elapsedTimeFormat,
            color,
            isExpired,
            isMissing: false,
            daysDiff
          }
        })

        const currentPermitLicenses = [...state.currentPermitLicenses]
        const typeIndex = currentPermitLicenses.findIndex(item => item.type === type)

        const typeData = {
          type,
          label: permitLicenseTypes[type] || type,
          permitLicenses: processedLicenses.sort((a, b) => {
            if (a.isMissing && !b.isMissing) return -1
            if (!a.isMissing && b.isMissing) return 1
            if (a.isExpired && !b.isExpired) return -1
            if (!a.isExpired && b.isExpired) return 1
            return a.daysDiff - b.daysDiff
          }),
          totalCount: processedLicenses.length,
          expiredCount: processedLicenses.filter(pl => pl.isExpired).length,
          missingCount: processedLicenses.filter(pl => pl.isMissing).length
        }

        if (typeIndex >= 0) {
          currentPermitLicenses[typeIndex] = typeData
        } else {
          currentPermitLicenses.push(typeData)
        }

        commit('SET_CURRENT_PERMIT_LICENSES', currentPermitLicenses)
      } catch (error) {
        const currentPermitLicenses = [...state.currentPermitLicenses]
        const typeIndex = currentPermitLicenses.findIndex(item => item.type === type)

        const emptyTypeData = {
          type,
          label: permitLicenseTypes[type] || type,
          permitLicenses: [],
          totalCount: 0,
          expiredCount: 0,
          missingCount: 0
        }

        if (typeIndex >= 0) {
          currentPermitLicenses[typeIndex] = emptyTypeData
        } else {
          currentPermitLicenses.push(emptyTypeData)
        }

        commit('SET_CURRENT_PERMIT_LICENSES', currentPermitLicenses)
      }
    },

    async fetchCurrentPermitLicenses({ dispatch }) {
      const permitLicenseTypes = companyDomains.methods.permitLicensesTypes()

      const promises = permitLicenseTypes.map(type =>
        dispatch('fetchCurrentPermitLicensesByType', { type: type.value })
      )
      await Promise.all(promises)
    },

    async pageChangeByType({ commit, dispatch }, { type, page }) {
      commit('SET_TYPE_CURRENT_PAGE', { type, page })
      await dispatch('fetchCurrentPermitLicensesByType', { type })
    },

    setPageSize({ commit, dispatch }, pageSize) {
      commit('SET_CURRENT_PAGE', 1)
      commit('SET_PAGE_SIZE', pageSize)
      dispatch('fetchCurrentPermitLicenses')
    },

    async pageChange({ commit, dispatch }, page) {
      commit('SET_CURRENT_PAGE', page)
      await dispatch('fetchCurrentPermitLicenses')
    },
  },
}
